import axios, { type AxiosResponse, type AxiosRequestConfig } from "axios"
import { STORAGE_KEYS } from "./StorageKeys";

class APIServiceHandler {
    private accessToken: string | null = null

    private async getAccessToken(): Promise<string | null> {
        try {
            return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        } catch (error) {
            console.error('Error reading access token', error);
            return null;
        }
    }

    private async request<T>(config: AxiosRequestConfig): Promise<T | null> {
        try {
            const accessToken = await this.getAccessToken();
            const headers = {
                ...config.headers,
                Accept: "application/json",
                ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
            };
            const response: AxiosResponse<T> = await axios({
                ...config,
                headers,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // Global 401 handler
                if (error.response && error.response.status === 401) {
                    // Remove tokens and force reload to logout everywhere
                    localStorage.clear();
                    // Optionally, show a message (could use a custom event or state)
                    window.location.href = "/login";
                    return null;
                }
                console.error("API call failed:", error.message);
                return error.response?.data || null;
            } else {
                console.error("An unexpected error occurred:", error);
                return null;
            }
        }
    }

    public async get<T>(url: string, params?: any): Promise<T | null> {
        return this.request<T>({
            method: "GET",
            url,
            params: typeof params === "string" ? JSON.parse(params) : params,
        })
    }

    public async post<T>(url: string, data?: any, headers?: Record<string, string>): Promise<T | null> {
        return this.request<T>({
            method: "POST",
            url,
            data: typeof data === "string" ? JSON.parse(data) : data,
            headers,
        })
    }

    public async put<T>(url: string, data?: any, headers?: Record<string, string>): Promise<T | null> {
        return this.request<T>({
            method: "PUT",
            url,
            data,
            headers,
        })
    }

    public async delete<T>(url: string): Promise<T | null> {
        return this.request<T>({
            method: "DELETE",
            url,
        })
    }
}

const apiServiceHandler = new APIServiceHandler()
export default apiServiceHandler

